// The call a notification was acted on, captured before anything can redirect.
//
// The service worker opens `#/staff/dashboard?notification_clicked=true&call_id=…` when no
// window is running. Reading it from the dashboard is too late: the route guard sends an
// unauthenticated boot to the sign-in screen (`<Navigate to="/staff" replace />`) and the
// sign-on round-trip returns to a fixed address, so the parameters are gone by the time any
// screen mounts. The application already had this exact problem with the auto-start marker
// (see setupState.captureAutostartMarker) and solves it the same way: read at boot, keep it,
// consume it once the session exists.
//
// The parameters live INSIDE the hash (the link is a hash route), so `window.location.search`
// is empty here — the query has to be taken from the fragment.
const KEY = 'qr_staff_incoming_call_link'

export interface IncomingCallLink {
  callId: string
  action: string
}

export const captureIncomingCallLink = (): void => {
  try {
    const hash = window.location.hash || ''
    const start = hash.indexOf('?')
    if (start === -1) return
    const params = new URLSearchParams(hash.slice(start + 1))
    const callId = params.get('call_id')
    if (!callId) return
    sessionStorage.setItem(KEY, JSON.stringify({ callId, action: params.get('action') || '' }))
  } catch {
    // Storage or URL APIs unavailable: the call simply is not recovered.
  }
}

// Read once. A second read returns nothing, so a reload never replays a call that is over.
export const consumeIncomingCallLink = (): IncomingCallLink | null => {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    sessionStorage.removeItem(KEY)
    const parsed = JSON.parse(raw)
    return parsed?.callId ? { callId: parsed.callId, action: parsed.action || '' } : null
  } catch {
    return null
  }
}
